import React, { useEffect, useState } from 'react';

const TOKEN_COLORS: Record<string, string> = {
    cro: '#002D74',
    btc: '#F7931A',
    eth: '#627EEA',
    sol: '#9945FF',
    matic: '#8247E5',
    usdc: '#2775CA',
    usd: '#2775CA',
};

export function getTokenColor(token: string): string {
    return TOKEN_COLORS[token.toLowerCase()] ?? '#666666';
}

/** Black or white fallback glyph, whichever contrasts more with the badge color. */
function letterColorFor(bg: string): string {
    const n = parseInt(bg.slice(1), 16);
    const [r, g, b] = [16, 8, 0].map((shift) => {
        const c = ((n >> shift) & 255) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const whiteRatio = 1.05 / (luminance + 0.05);
    const blackRatio = (luminance + 0.05) / 0.05;
    return whiteRatio >= blackRatio ? '#ffffff' : '#0A0B09';
}

/** Ticker without the builder-dex namespace — "xyz:SP500" -> "SP500". */
function tickerOf(token: string): string {
    const separator = token.indexOf(':');
    return separator >= 0 ? token.slice(separator + 1) : token;
}

/**
 * Icon filename for a token: vendored under public/tokens/ (see
 * scripts/fetch-token-icons.mjs). "k"-prefixed HL 1000x tokens (kPEPE) use the
 * base token's icon; the quote leg "usd" renders as USDC.
 *
 * Namespaced HIP-3 coins KEEP their dex prefix, as "xyz-sp500". Keying on the
 * bare ticker looked tidier but is wrong twice over: the equity markets collide
 * with same-named crypto tokens (xyz:META is Meta Platforms, META is a
 * different asset entirely), and Hyperliquid only serves these logos under the
 * namespaced name — app.hyperliquid.xyz/coins/SP500.svg is a 404 page, while
 * .../coins/xyz:SP500.svg is the real mark. The colon becomes a dash so the
 * filename is safe on every filesystem and needs no URL escaping in `src`.
 */
function iconSlug(token: string): string {
    const namespaced = token.includes(':');
    const ticker = tickerOf(token);
    const base = /^k[A-Z0-9]/.test(ticker) ? ticker.slice(1) : ticker;
    const lower = base.toLowerCase();
    if (namespaced) {
        return `${token.slice(0, token.indexOf(':')).toLowerCase()}-${lower}`;
    }
    return lower === 'usd' ? 'usdc' : lower;
}

export function TokenIcon({
    token,
    size = 'md',
    className = '',
}: {
    token: string;
    /** 'sm' = 14px (trade rows), 'md' = 20px (default/dropdown) */
    size?: 'sm' | 'md';
    className?: string;
}) {
    const slug = iconSlug(token);
    // Vendored icons are .png or .svg depending on source; try both before
    // falling back to the letter circle.
    const candidates = [`/tokens/${slug}.png`, `/tokens/${slug}.svg`];
    const [sourceIndex, setSourceIndex] = useState(0);

    useEffect(() => {
        setSourceIndex(0);
    }, [slug]);

    const ticker = tickerOf(token);
    const color = getTokenColor(ticker);
    const initial = ticker.charAt(0).toUpperCase();
    const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
    const textClass = size === 'sm' ? 'text-[8px]' : 'text-[10px]';

    if (sourceIndex < candidates.length) {
        return (
            <img
                src={candidates[sourceIndex]}
                alt=""
                loading="lazy"
                onError={() => setSourceIndex((i) => i + 1)}
                className={`${sizeClass} shrink-0 rounded-full bg-secondary object-cover ${className}`}
            />
        );
    }

    return (
        <div
            className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-full ${className}`}
            style={{ backgroundColor: color }}
        >
            <span
                className={`${textClass} font-bold`}
                style={{ color: letterColorFor(color) }}
            >
                {initial}
            </span>
        </div>
    );
}
